import React from "react";
import { Alert, Col, Container, Row, Spinner } from "reactstrap";

import CharacterAvatar from "../../../Components/Common/CharacterAvatar";
import { getBattles } from "../../../helpers/api/battle";
import { sortBattleParticipations } from "../../../helpers/battle/participations";
import type { BattleListItem, BattleParticipation } from "../../../types/api.generated";

export const BATTLES_PAGE_SIZE = 20;

export type BattleHistoryParticipation = BattleParticipation;
export type BattleHistoryItem = BattleListItem;

export const getParticipationVolume = (participation?: BattleHistoryParticipation) => {
  const value = participation?.pool.totalVolume ?? 0;
  return Number.isFinite(Number(value)) ? Number(value) : 0;
};

export const getBattleTotalVolume = (participations: BattleHistoryParticipation[]) => {
  return participations.reduce((total, participation) => total + getParticipationVolume(participation), 0);
};

export const getDecimalOdd = (sideVolume: number, totalVolume: number) => {
  if (sideVolume <= 0 || totalVolume <= 0) return 1;
  return totalVolume / sideVolume;
};

const formatPoints = (value: number) => {
  return new Intl.NumberFormat("fr-FR").format(Math.round(value));
};

const formatBattleDateTime = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getStatusClass = (status?: string) => {
  const normalized = String(status ?? "").toLowerCase();
  if (["active", "live", "in_progress"].includes(normalized)) return "battle-history-status battle-history-status--live";
  if (["scheduled", "upcoming", "pending"].includes(normalized)) return "battle-history-status battle-history-status--upcoming";
  return "battle-history-status";
};

const getCharacterName = (participation?: BattleHistoryParticipation, fallback = "Unknown") => {
  return participation?.character.name || fallback;
};

const BattleAvatar = ({ participation, side }: { participation?: BattleHistoryParticipation; side: "left" | "right" }) => {
  const name = getCharacterName(participation);

  return (
    <div className={`battle-history-fighter battle-history-fighter--${side}`}>
      <div className="battle-history-avatar">
        <CharacterAvatar avatar={participation?.character.avatar} name={name} />
      </div>
      <strong>{name}</strong>
    </div>
  );
};

const BattleHistoryCard = ({ battle }: { battle: BattleHistoryItem }) => {
  const participations = sortBattleParticipations(battle.participations ?? []).slice(0, 2);
  const left = participations[0];
  const right = participations[1];
  const leftName = getCharacterName(left, "Fighter 1");
  const rightName = getCharacterName(right, "Fighter 2");
  const leftVolume = getParticipationVolume(left);
  const rightVolume = getParticipationVolume(right);
  const totalVolume = getBattleTotalVolume(participations);
  const leftPercent = totalVolume > 0 ? (leftVolume / totalVolume) * 100 : 50;
  const rightPercent = Math.max(0, 100 - leftPercent);

  return (
    <article className="battle-history-card" data-cy="battle-history-card" data-testid="battle-history-card">
      <header className="battle-history-card__header">
        <time dateTime={battle.startTime}>{formatBattleDateTime(battle.startTime)}</time>
        <span className={getStatusClass(battle.status)}>{battle.status ?? "unknown"}</span>
      </header>

      <div className="battle-history-versus">
        <BattleAvatar participation={left} side="left" />
        <span className="battle-history-vs">VS</span>
        <BattleAvatar participation={right} side="right" />
      </div>

      <div
        className="battle-history-volume-bar"
        aria-label={`${leftName} ${formatPoints(leftVolume)} points, ${rightName} ${formatPoints(rightVolume)} points`}
      >
        <span style={{ width: `${leftPercent}%` }} />
        <span style={{ width: `${rightPercent}%` }} />
      </div>

      <div className="battle-history-volume-labels">
        <span>{leftName}</span>
        <span>{rightName}</span>
      </div>

      <footer className="battle-history-card__footer">
        <div>
          <span>Pool</span>
          <strong>{formatPoints(totalVolume)} PTS</strong>
        </div>
        <div>
          <span>Odds</span>
          <strong>
            <b>{getDecimalOdd(leftVolume, totalVolume).toFixed(2)}x</b>
            <i>/</i>
            <b>{getDecimalOdd(rightVolume, totalVolume).toFixed(2)}x</b>
          </strong>
        </div>
        <a className="battle-history-view" href={`/battles/${battle.id}`} data-cy="battle-history-view">
          View
        </a>
      </footer>
    </article>
  );
};

const Battles = () => {
  document.title = "Legends | The Colosseum - Battles";

  const [battles, setBattles] = React.useState<BattleHistoryItem[]>([]);
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState<number | undefined>(undefined);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const loadingRef = React.useRef(false);
  const hasUserScrolledRef = React.useRef(false);

  const hasMore = typeof total === "number" ? battles.length < total : battles.length === 0 || battles.length >= page * BATTLES_PAGE_SIZE;

  const loadBattles = React.useCallback(async (nextPage: number) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const response = await getBattles({ page: nextPage, limit: BATTLES_PAGE_SIZE });
      setTotal(response.meta.totalItems);
      setBattles((current) => (nextPage === 1 ? response.items : [...current, ...response.items]));
      setPage(nextPage);
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message || "Failed to load battles");
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadBattles(1);
  }, [loadBattles]);

  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || loading) return;

    const observer = new IntersectionObserver((entries) => {
      if (hasUserScrolledRef.current && entries.some((entry) => entry.isIntersecting)) {
        loadBattles(page + 1);
      }
    }, { rootMargin: "240px" });

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadBattles, loading, page]);

  React.useEffect(() => {
    const handleScroll = () => {
      hasUserScrolledRef.current = true;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="page-content battle-history-page" data-cy="battle-history-page">
      <Container fluid>
        <div className="battle-history-heading">
          <h1>Battles</h1>
        </div>

        {error && <Alert color="danger" role="alert">{error}</Alert>}

        <Row className="g-4">
          {battles.map((battle, index) => (
            <Col xs={12} lg={6} xl={4} key={battle.id ?? `${battle.startTime}-${index}`}>
              <BattleHistoryCard battle={battle} />
            </Col>
          ))}
        </Row>

        {!loading && battles.length === 0 && !error && (
          <div className="battle-history-empty" data-cy="battle-history-empty" data-testid="battle-history-empty">No battles found</div>
        )}

        <div ref={sentinelRef} className="battle-history-sentinel" data-cy="battle-history-sentinel">
          {loading && (
            <span>
              <Spinner size="sm" /> Loading battles...
            </span>
          )}
        </div>
      </Container>
    </div>
  );
};

export default Battles;
